import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AvailableResult, NativeBiometric } from "capacitor-native-biometric";
import { Observable, catchError, of, retry, shareReplay, tap } from 'rxjs';
import { User } from 'src/models/user';
import { AppSettings } from '../app-settings';
import { Menu } from 'src/models/menu';
import { ApplicationVersion } from 'src/models/applicationVersion';
import { MyEventServices } from './my-event.service';
import { environment } from 'src/environments/environment';
import { ParishConfigService } from './parishconfig.service';

@Injectable({
    providedIn: 'root'
})
export class LoginService {
    public logonComplete = new EventEmitter<any>();
    public logoutComplete = new EventEmitter<number>();

    currentUser: User = {} as User;
    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    };

    constructor(private http: HttpClient, private route: Router, private appSettings: AppSettings,
        private parishConfigService: ParishConfigService,
        private myEventService: MyEventServices, private router: Router) { }

    validateSession() {
        if (this.currentUser && this.currentUser.UserId > 0) {
            return true;
        }

        return false;
    }

    isBiometricAvailable(): Promise<boolean> {
        return new Promise((resolve, reject) => NativeBiometric.isAvailable().then((result: AvailableResult) => {
            return resolve(result.isAvailable);
        }));
    }

    checkCredential(): Promise<boolean | any> {
        return new Promise((resolve, reject) => {
            NativeBiometric.isAvailable().then((result: AvailableResult) => {
                const isAvailable = result.isAvailable;

                if (isAvailable) {
                    // Get user's credentials
                    NativeBiometric
                        .getCredentials({ server: `koinonia_${this.parishConfigService.getCurrentParishConfig()?.ParishId}` })
                        .then((credentials) => {
                            // Authenticate using biometrics before logging the user in
                            resolve(credentials);
                        }).catch((err) => {
                            reject(false);
                        });
                }
            });
        });
    }

    loginWithBiometric(): Promise<boolean | null> {
        return new Promise((resolve, reject) => {
            NativeBiometric.verifyIdentity({
                reason: 'For easy log in',
                title: 'Log in',
                subtitle: environment.appName,
                description: '',
                maxAttempts: 3
            }).then(() => {
                sessionStorage.setItem("loginvalidation", 'true');
                this.checkCredential().then(credentails => {
                    if (credentails) {
                        this.login(credentails.username, credentails.password, true).subscribe();
                    }
                }).catch(err => {
                    console.error(err);
                });
                resolve(true);
            }).catch((err) => {
                reject(false);
            });
        });
    }

    setCredential(username: string, password: string) {
        // Save user's credentials
        NativeBiometric.setCredentials({
            username: username,
            password: password,
            server: `koinonia_${this.parishConfigService.getCurrentParishConfig()?.ParishId}`,
        }).then();

        sessionStorage.setItem("loginvalidation", 'true');
    }

    deleteCredential() {
        NativeBiometric.deleteCredentials({
            server: `koinonia_${this.parishConfigService.getCurrentParishConfig()?.ParishId}`,
        });
    }

    login(username: string, password: string, isUseBiometric: boolean): Observable<User | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/Signin?skipLoadingSpinner=1&username=' + username + "&password=" + encodeURIComponent(password);

        return this.http.get<User>(url, this.httpOptions)
            .pipe(
                tap(result => {
                    if (result && result.UserId > 0) {
                        result.UserPassword = password;
                        if (isUseBiometric) {
                            this.setCredential(username, password)
                        }

                        this.clearLoginData();
                        this.setCurrentUser(result, isUseBiometric);
                        this.router.navigateByUrl("/");
                    }
                    else if (result == null || result == undefined) {
                        this.myEventService.showToast("error", "Login failed", "Unable to connect to serivce, please try again later");
                    }
                    else if (result && result.UserId == 0) {
                        this.myEventService.showToast("error", "Login failed", "Invalid username or password");
                    }
                }),
                catchError(this.handleError<null>('login', null))
            );
    }

    isAppInReview(): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Public/IsAppInAppleReview?skipLoadingSpinner=1';
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    changeUserPassword(username: string, currentPassword: string, password: string): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/ChangePassword?username=' + username +
            '&currentPassword=' + encodeURIComponent(currentPassword) + '&newPassword=' + encodeURIComponent(password);

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay());
    }

    resetUserPassword(username: string, password: string): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/ResetUserPassword?username=' + username + '&newPassword=' + encodeURIComponent(password);

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay());
    }

    logout(redirectToHome: boolean = true) {
        if (this.currentUser && this.currentUser.UserId > 0) {
            let profileId = JSON.parse(JSON.stringify(this.currentUser?.UserId.toString()));

            this.DeleteUserDevice(profileId).subscribe(() => {
                this.clearLoginData();
                this.logoutComplete.emit(profileId);

                if (redirectToHome) {
                    this.route.navigateByUrl("home");
                }

            }, (error) => {
                // even if the call crashes, clear out
                this.clearLoginData();
                this.logoutComplete.emit(profileId);

                if (redirectToHome) {
                    this.route.navigateByUrl("home");
                }
            });
        }
    }

    clearLoginData() {
        this.currentUser = {} as User;
        localStorage.removeItem("currentuser");
        localStorage.removeItem('myclass')
        sessionStorage.removeItem("menu");
        sessionStorage.removeItem("loginvalidation");
        sessionStorage.removeItem("token");
    }

    getUserByUserID(id: number): Observable<User | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/GetUserByUserID?id=' + id;
        return this.http.get<User>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('getUserByUserID', null))
            );
    }

    getUserMenu(id: number, isForceGet = false): Observable<Menu[] | null> {
        let mnu = sessionStorage.getItem("menu");

        if (mnu && isForceGet === false) {
            return of(JSON.parse(mnu));
        }
        else {
            let url = this.appSettings.apiBaseUrl() + 'Auth/GetUserMenu?skipLoadingSpinner=1&id=' + id;

            return this.http.get<Menu[]>(url, this.httpOptions)
                .pipe(
                    retry({ count: 2, delay: 1000 }),
                    shareReplay(),
                    tap(result => {
                        sessionStorage.setItem("menu", JSON.stringify(result));
                    }),
                    catchError(this.handleError<null>('getusermenu', null))
                );
        }
    }

    insertNewUser(profileId: number, profileName: string, mobile: string, email: string, passowrd: string, guid: number): Observable<number | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/InsertNewUser?profileId=' + profileId + '&profileName=' + profileName +
            '&mobile=' + mobile + '&email=' + email + '&password=' + passowrd + "&guid=" + guid;

        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay());
    }

    saveUserDevice(profileId: number, manufacturer: string, model: string, version: string, uuid: string, appVersion: string) {
        let url = this.appSettings.apiBaseUrl() + 'Auth/SaveUserDevice?skipLoadingSpinner=1&profileId=' + profileId + '&manufacturer=' + manufacturer +
            '&model=' + model + '&version=' + version + '&uuid=' + uuid + '&appVersion=' + appVersion;

        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay());
    }

    DeleteUserDevice(profileId: number) {
        let url = this.appSettings.apiBaseUrl() + 'Auth/DeleteUserDevice?skipLoadingSpinner=1&profileId=' + profileId;

        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay());
    }

    getApplicationVersion(): Observable<ApplicationVersion | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/GetApplicationVersion?skipLoadingSpinner=1&';

        return this.http.get<ApplicationVersion>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }),
                shareReplay());
    }

    saveLoginToken(username: string, token: string): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/SaveFingerprintToken?username=' + username + '&token=' + token;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('saveLoginToken', null)), retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    getFingerprintTokenByUsername(username: string): Observable<string | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/GetFingerprintToken?username=' + username;

        return this.http.get<string>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('getFingerprintToken', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    sendForgotPasswordEmail(username: string): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/SendForgotPassword?username=' + username;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('sendForgotPasswordEmail', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    GetForgotPasswordExpiration(guid: string, userName: string): Observable<string | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/GetForgotPasswordExpiration?guid=' + guid + '&userName=' + userName;

        return this.http.get<string>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('GetForgotPasswordExpiration', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    isUserNameAlreadyRegistered(userName: string): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/IsUserNameAlreadyRegistered?userName=' + userName;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('isUserNameAlreadyRegistered', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    sendRegistrationCode(email: string): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Auth/SendRegistrationCode?email=' + email;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('SendRegistrationCode', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    getCurrentUser() {
        let strUser = localStorage.getItem("currentuser");

        if (strUser) {
            let user = JSON.parse(strUser);
            this.currentUser = user;
        }
        else {
            this.currentUser = {} as User;
        }

        return this.currentUser;
    }

    setCurrentUser(user: User, isUseBiometric: boolean) {
        this.currentUser = user;
        localStorage.setItem("currentuser", JSON.stringify(user));
        localStorage.setItem("usebiometric", JSON.stringify(isUseBiometric));
        this.logonComplete.emit();
    }

    deleteAccount(userId: number): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/DeleteAccount?userId=' + userId;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('deleteAccount', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    deleteProfile(profileId: number): Observable<boolean | null> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/DeleteProfile?profileId=' + profileId;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                catchError(this.handleError<null>('deleteAccount', null)),
                retry({ count: 2, delay: 500 }),
                shareReplay()
            );
    }

    getUserNotificationCount(): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetUserNotificationCount?skipLoadingSpinner=1';
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 }),
                shareReplay()
            );
    }

    saveAppVersion(applicationVersion: string): Observable<void> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/UpdateInstalledVersion?skipLoadingSpinner=1&version=' + applicationVersion;
        return this.http.get<void>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 1000 }),
                shareReplay()
            );
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            console.error(`${operation} failed: ${error.message}`);
            return of(result as T);
        };
    }
}